import { useEffect, useState } from 'react';

import type { PageSummary } from '@/api/client';
import { wikiApiClient } from '@/api/client';
import { buildPath, defaultLang } from '@/app/routes';
import { LIMITS } from '@/config';
import { useT } from '@/i18n/LocaleProvider';

export type AllPagesProps = {
  lang: string;
  prefix?: string;
  homeSlug?: string;
  onNavigate: (slug: string) => void;
  onSelectPrefix: (prefix: string | undefined) => void;
};

type LetterGroup = {
  letter: string;
  pages: PageSummary[];
};

export function AllPages({ lang, prefix, homeSlug, onNavigate, onSelectPrefix }: AllPagesProps) {
  const t = useT();
  const [pages, setPages] = useState<PageSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);

      try {
        const result = await wikiApiClient.listPages(lang, {
          limit: LIMITS.pageLimit,
          prefix: prefix || undefined,
        });

        if (!cancelled) {
          setPages(result.pages);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('error_load_pages'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [lang, prefix, t]);

  const groups = groupByFirstLetter(pages);
  const letters = groups.map((g) => g.letter);

  return (
    <article className="note page-index" data-testid="all-pages">
      <header className="note-header">
        <div className="note-kicker">
          <span>
            {pages.length === 1
              ? t('page_index_page_count_one')
              : t('page_index_page_count_many', { count: pages.length })}
          </span>
        </div>
        <h1>{t('page_index_all')}</h1>
      </header>

      {letters.length > 0 ? (
        <nav aria-label="Letter index" className="all-pages-letter-index">
          <button
            type="button"
            className={!prefix ? 'is-active' : undefined}
            onClick={() => onSelectPrefix(undefined)}
          >
            #
          </button>
          {letters.map((letter) => (
            <button
              key={letter}
              type="button"
              className={prefix === letter ? 'is-active' : undefined}
              onClick={() => onSelectPrefix(letter === prefix ? undefined : letter)}
            >
              {letter}
            </button>
          ))}
        </nav>
      ) : null}

      {isLoading ? <div className="empty-note">{t('page_index_loading')}</div> : null}
      {!isLoading && error ? <div className="empty-note">{error}</div> : null}
      {!isLoading && !error && pages.length === 0 ? (
        <div className="empty-note">{t('page_index_empty')}</div>
      ) : null}

      {!isLoading && !error && groups.length > 0
        ? groups.map((group) => (
            <section key={group.letter} className="all-pages-group">
              <h2 className="all-pages-group-letter">{group.letter}</h2>
              <ul className="page-index-list">
                {group.pages.map((page) => (
                  <li key={page.id || page.slug}>
                    <a
                      href={buildPath(
                        { kind: 'reader', slug: page.slug, lang: lang ?? defaultLang },
                        homeSlug,
                        lang,
                      )}
                      onClick={(event) => {
                        event.preventDefault();
                        onNavigate(page.slug);
                      }}
                    >
                      <strong>{page.title}</strong>
                      {page.is_stub ? <span>{t('page_index_stub')}</span> : null}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ))
        : null}
    </article>
  );
}

function groupByFirstLetter(pages: PageSummary[]): LetterGroup[] {
  const map = new Map<string, PageSummary[]>();

  for (const page of pages) {
    const letter = firstLetter(page.title);
    const existing = map.get(letter);
    if (existing) {
      existing.push(page);
    } else {
      map.set(letter, [page]);
    }
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b, 'ru'))
    .map(([letter, ps]) => ({ letter, pages: ps }));
}

function firstLetter(title: string): string {
  const ch = title.charAt(0);
  if (!ch) return '#';
  const upper = ch.toLocaleUpperCase('ru');
  return /\p{L}/u.test(upper) ? upper : '#';
}
